import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { Chart, registerables } from "chart.js";
import {
  computeDashboard,
  type DashboardAssignment,
  type DashboardFieldProgress,
  type DashboardFilters,
  type DashboardIndicators,
  type DashboardUnitArea
} from "../../domain/dashboard/dashboard-indicators.js";
import {
  WorkedDaysSource,
  type InspectorDailyRate,
  type InspectorPerformanceReport,
  type WorkedDayEntry
} from "../../domain/dashboard/inspector-performance.js";
import {
  buildInspectorRanking,
  rdoFiltersFor,
  toDashboardAssignments,
  toDashboardFieldProgress,
  toDashboardInspections
} from "../../domain/dashboard/dashboard-inputs.js";
import { ContextLoadTracker, CONTEXT_LOAD_STYLES, loadList, type ContextList } from "../context/context-load.js";
import "../dashboard/dashboard-share-panel.js";

Chart.register(...registerables);

/** Paleta sóbria: grafite/cinza; cor só como indicador pequeno (barra lateral, ponto, ícone). */
const INK = {
  text: "#E7EAED",
  muted: "#9BA4AE",
  grid: "rgba(255,255,255,0.06)",
  planned: "#8C99A6",
  real: "#E7EAED",
  bars: "rgba(255,255,255,0.16)",
  barsBorder: "rgba(255,255,255,0.24)",
  done: "#6E9E7E",
  pending: "#B29455",
  out: "#A76F6F"
};

export interface Option { id: string; label: string; clientId?: string; projectId?: string; type?: string; startDate?: string | null; expectedEndDate?: string | null; }

/**
 * Exportada para a visualização pública do link compartilhado
 * (`shared-dashboard-page.ts`) herdar os MESMOS cards, gráficos e ranking —
 * ela troca só a origem dos dados, o cabeçalho e os filtros. Duas telas
 * desenhadas separadamente divergiriam com o tempo.
 */
export class OverviewPage extends HTMLElement {
  protected curveChart: Chart | null = null;
  protected statusChart: Chart | null = null;

  protected isLoading = true;
  protected loadError = "";
  protected lastUpdatedAt: Date | null = null;

  protected projects: Option[] = [];
  protected unitAreas: Array<Option & { projectId: string }> = [];
  /** CLIENTES autorizados — fonte do filtro local e da agregação multicliente. */
  protected clients: Option[] = [];
  /** INSPETORES candidatos ao filtro e ao ranking (Professional, não User). */
  protected inspectors: Option[] = [];
  /** Aviso de falha parcial na carga multicliente (ContextLoadTracker não possui setError). */
  protected multiclientWarning: string | null = null;

  /** Leitura de contexto classificada: negada ≠ "não há cadastro". */
  protected readonly ctxLoad = new ContextLoadTracker();
  /** Ranking de avanço por Inspetor e qual linha está aberta no detalhamento. */
  protected performance: InspectorPerformanceReport | null = null;
  protected expandedInspector: string | null = null;
  /** Desfecho da leitura dos vínculos — explicado dentro do card do ranking. */
  protected assignmentsLoad: ContextList<unknown> | null = null;
  /** Desfecho da leitura do RDO — define se o ritmo é real ou derivado. */
  protected rdoLoad: ContextList<unknown> | null = null;

  /** Filtros aplicados (o que os indicadores refletem) e o rascunho do formulário. */
  protected applied: DashboardFilters = {};
  protected draft: DashboardFilters = {};

  protected data: DashboardIndicators | null = null;
  protected showAllUnits = false;
  protected unsubscribe: (() => void) | null = null;

  /**
   * TROCA DE CLIENTE. Projeto e Unidade / Área filtrados pertencem a UM
   * Cliente: mantidos depois da troca, recortariam o Cliente novo por um
   * Projeto que ele não tem e o painel ficaria zerado. E as cargas são
   * assíncronas — a resposta do Cliente anterior que chegar depois da do
   * novo é descartada pela geração, nunca desenhada por cima.
   */
  protected scopeClientId: string | null = null;
  protected optionsGeneration = 0;
  protected dataGeneration = 0;

  async connectedCallback() {
    this.scopeClientId = contextStore.get().activeClientId || null;
    // Assina ANTES da primeira carga: o Cliente ativo resolvido no login/F5
    // pode chegar enquanto ela está em andamento e não pode ser perdido.
    this.unsubscribe = contextStore.subscribe(async () => {
      this.resetClientScopedFilters();
      await this.loadFilterOptions();
      await this.loadData();
    });
    this.render();
    await this.loadFilterOptions();
    await this.loadData();
  }

  /**
   * [MELHORIA ESPECÍFICA] Na arquitetura de filtro global, o Projeto é
   * selecionado localmente e deriva seu próprio clientId. A troca do
   * activeClientId global NÃO deve mais limpar projeto/unidade, pois o
   * contexto agora vem do projeto selecionado, não do seletor global.
   * Mantém scopeClientId atualizado para compatibilidade com outros fluxos.
   */
  protected resetClientScopedFilters() {
    const clientId = contextStore.get().activeClientId || null;
    if (clientId === this.scopeClientId) return;
    this.scopeClientId = clientId;
    // Não limpa projectId/unitAreaId: o filtro global de projetos não
    // depende do activeClientId global. A troca de contexto apenas dispara
    // recarregamento via subscriber em connectedCallback.
  }

  disconnectedCallback() {
    this.destroyCharts();
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  protected destroyCharts() {
    this.curveChart?.destroy();
    this.curveChart = null;
    this.statusChart?.destroy();
    this.statusChart = null;
  }

  protected get platform(): any {
    return (window as any).app?.platform;
  }

  /**
   * [MELHORIA ESPECÍFICA] Contexto de requisição. Aceita clientId opcional
   * para telas com filtro local próprio (Projeto → Cliente derivado),
   * eliminando dependência do seletor global de Cliente.
   */
  protected ctx(clientId?: string): any {
    const c = contextStore.get();
    return { activeClientId: clientId || c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  protected can(permission: string): boolean {
    return contextStore.hasPermission(permission as any);
  }

  protected async loadFilterOptions() {
    const generation = ++this.optionsGeneration;
    const p = this.platform;
    const ctx = this.ctx();
    try {
      // [MELHORIA ESPECÍFICA] Carrega projetos de TODOS os clientes autorizados
      // sem depender do seletor global de Cliente. O clientId é derivado do
      // projeto selecionado, não do contexto global.
      // Lista projetos sem filtro de clientId (backend retorna apenas os autorizados)
      const projects: any[] = (await p?.projectService?.listProjects(undefined, undefined, ctx).catch(() => [])) || [];
      // Carrega nomes dos clientes autorizados para enriquecer o label
      const clients: any[] = (await p?.adminService?.listAuthorizedClients(ctx).catch(() => [])) || [];
      const clientNameMap = new Map<string, string>();
      for (const c of clients) clientNameMap.set(String(c.id), c.name || c.legalName || String(c.id));
      // Unidades/Áreas carregadas sem filtro de clientId (todas as autorizadas)
      const units: any[] = (await p?.projectService?.listUnitAreas({}, undefined, ctx).catch(() => [])) || [];
      if (generation !== this.optionsGeneration) return;
      // PAINEL MULTICLIENTE: popula this.clients com todos os Clientes autorizados
      // para o filtro local e agregação (seções 4 e 9).
      this.clients = clients.map((c: any) => ({
        id: String(c.id),
        label: c.name || c.legalName || String(c.id),
        clientId: String(c.id)
      }));
      this.projects = projects.map(pr => {
        const projectName = [pr.code, pr.name].filter(Boolean).join(" - ") || String(pr.id);
        const clientName = clientNameMap.get(String(pr.clientId)) || "";
        // Formato: PROJETO — CLIENTE (conforme especificação)
        const label = clientName ? `${projectName} — ${clientName}` : projectName;
        return {
          id: String(pr.id),
          label,
          clientId: String(pr.clientId || ""),
          projectId: String(pr.id),
          startDate: pr.startDate || null,
          expectedEndDate: pr.expectedEndDate || null
        };
      });
      this.unitAreas = units.map(u => ({
        id: String(u.id),
        label: [u.code, u.name].filter(Boolean).join(" - ") || String(u.id),
        clientId: String(u.clientId || ""),
        projectId: String(u.projectId || "")
      }));

      // INSPETORES: Professional é GLOBAL. Usa listEligibleInspectors (fonte
      // oficial do Controle de Campo) em vez de listProfessionals que filtra
      // por activeClientId (seção 17 do comando oficial revisado).
      const eligibleInspectors = await this.ctxLoad.load<any>(
        "Inspetores Elegíveis",
        () => p?.fieldControlService?.listEligibleInspectors(ctx)
      );
      if (generation !== this.optionsGeneration) return;
      this.inspectors = eligibleInspectors
        .map((prof: any) => ({
          id: String(prof.id),
          label: String(prof.name || prof.id),
          type: String(prof.professionalType || "")
        }))
        .sort((a: Option, b: Option) => a.label.localeCompare(b.label, "pt-BR"));
    } catch (e) {
      console.warn("[DASHBOARD] Não foi possível carregar as opções de filtro", e);
    }
  }

  /**
   * REGRESSÃO V3-A1/A2: aplica imediatamente os filtros do draft,
   * copiando para applied e recarregando os indicadores.
   * Usado pelos listeners de Cliente/Projeto/Unidade/Inspetor para
   * garantir que a troca do select dispare nova carga sem exigir
   * clique manual em "Aplicar Filtros".
   */
  protected async applyDraftFilters(): Promise<void> {
    this.applied = { ...this.draft };
    await this.loadData();
  }

  protected async loadData() {
    const generation = ++this.dataGeneration;

    // PAINEL MULTICLIENTE: resolve targetClientIds pela ordem de prioridade:
    // 1. applied.clientId explícito → somente aquele Cliente
    // 2. applied.projectId → clientId derivado do Projeto
    // 3. Nenhum filtro → TODOS os Clientes autorizados (this.clients)
    // NÃO usa activeClientId global como fallback.
    let targetClientIds: string[];
    if (this.applied.clientId) {
      targetClientIds = [this.applied.clientId];
    } else if (this.applied.projectId) {
      const proj = this.projects.find(p => p.id === this.applied.projectId);
      targetClientIds = proj?.clientId ? [proj.clientId] : [];
    } else {
      targetClientIds = this.clients.map(c => c.id).filter(Boolean);
    }

    if (targetClientIds.length === 0) {
      this.isLoading = false;
      this.loadError = "";
      this.data = null;
      this.performance = null;
      this.render();
      return;
    }

    this.isLoading = true;
    this.loadError = "";
    this.render();

    const p = this.platform;
    try {
      // CARGA CONSOLIDADA: uma consulta por Cliente autorizado, em paralelo.
      // Cada chamada usa ctxFor(clientId) com activeClientId explícito e validado.
      // O recorte por projectId/unitAreaId/inspectorId/período é feito pelo
      // computeDashboard sobre o conjunto já autorizado (seção 11).
      const perClientResults = await Promise.allSettled(
        targetClientIds.map(async (clientId) => {
          const ctx = this.ctx(clientId);
          const [equipments, rawInspections, reports, recommendations, fieldScope, rawAssignments, rawRdo] = await Promise.all([
            p?.equipmentService?.listEquipments(ctx, { clientId }).catch(() => []) ?? [],
            p?.inspectionService?.listAllInspections(ctx).catch(() => []) ?? [],
            p?.reportService?.listReports(ctx).catch(() => []) ?? [],
            p?.reportService?.listAllRecommendations(ctx).catch(() => []) ?? [],
            this.can(PermissionCode.FieldControlViewAll)
              ? p?.fieldControlService?.listScope(ctx, { clientId }).catch(() => null) ?? null
              : Promise.resolve(null),
            this.can(PermissionCode.FieldControlViewAll) || this.can(PermissionCode.FieldControlViewOwn) || this.can(PermissionCode.FieldControlManageAssignments)
              ? loadList<any>("os Vínculos de Inspetor", () => p?.fieldControlService?.listAssignments(ctx, {}))
              : Promise.resolve({ items: [], status: "ok" as const, message: "", placeholder: "" }),
            loadList<WorkedDayEntry>("os apontamentos de RDO", () => p?.rdoService?.listRdos(ctx, rdoFiltersFor({ ...this.applied, clientId })))
          ]);
          return { clientId, equipments, rawInspections, reports, recommendations, fieldScope, rawAssignments, rawRdo };
        })
      );

      if (generation !== this.dataGeneration) return;

      // FALHA PARCIAL: um Cliente com erro NÃO zera os demais (seção 15).
      const failedClients: string[] = [];
      const allEquipments: any[] = [];
      const allInspections: any[] = [];
      const allReports: any[] = [];
      const allRecommendations: any[] = [];
      const allFieldRows: any[] = [];
      const allSystems: any[] = [];
      const allAssignments: any[] = [];
      const allRdoItems: any[] = [];
      let lastRdoLoad: ContextList<unknown> | null = null;
      let lastAssignmentsLoad: ContextList<unknown> | null = null;

      for (const result of perClientResults) {
        if (result.status === "fulfilled") {
          const r = result.value;
          // CORRETIVO P0: proteger contra coleções undefined em cenários de falha parcial
          allEquipments.push(...(r.equipments ?? []));
          allInspections.push(...(r.rawInspections ?? []));
          allReports.push(...(r.reports ?? []));
          allRecommendations.push(...(r.recommendations ?? []));
          if (r.fieldScope?.rows) allFieldRows.push(...r.fieldScope.rows);
          if (r.fieldScope?.systems) allSystems.push(...r.fieldScope.systems);
          if (r.rawAssignments?.items) allAssignments.push(...r.rawAssignments.items);
          if (r.rawRdo?.items) allRdoItems.push(...r.rawRdo.items);
          if (r.rawRdo) lastRdoLoad = r.rawRdo;
          if (r.rawAssignments) lastAssignmentsLoad = r.rawAssignments;
        } else {
          // Extrai clientId do rejection se disponível
          failedClients.push("desconhecido");
        }
      }

      if (failedClients.length > 0) {
        this.multiclientWarning = `Não foi possível carregar dados de ${failedClients.length} cliente(s). Os demais foram carregados normalmente.`;
      }

      const unitAreas: DashboardUnitArea[] = this.unitAreas.map(u => ({
        id: u.id,
        clientId: (u as any).clientId ?? null,
        code: "",
        name: u.label,
        projectId: u.projectId
      }));

      // Mapa de equipamentos por ID para resolver clientId/projectId/unitAreaId
      // legados no FieldProgress quando o apontamento não possui esses campos (seção 18).
      const equipmentById = new Map(allEquipments.map(eq => [
        eq.id,
        { projectId: eq.projectId, unitAreaId: eq.unitAreaId, areaId: (eq as any).areaId, clientId: eq.clientId }
      ]));

      const fieldProgress: DashboardFieldProgress[] = toDashboardFieldProgress(allFieldRows, equipmentById);
      const inspections = toDashboardInspections(allInspections as any[]);
      this.assignmentsLoad = lastAssignmentsLoad;
      const assignments: DashboardAssignment[] = toDashboardAssignments(allAssignments);
      this.rdoLoad = lastRdoLoad;

      this.data = computeDashboard(
        { equipments: allEquipments, inspections, reports: allReports, recommendations: allRecommendations, unitAreas, fieldProgress, assignments },
        this.applied
      );
      // REGRA V4-COMPLEMENTO: Mapa lineEquipmentId → TAG real para drill-down
      // no breakdown por Inspetor (Sistemas reais e OFF-SITE/TUBOVIA).
      // Reutiliza allEquipments já carregado; evita N+1.
      const lineLabels = new Map<string, string>();
      for (const eq of allEquipments) {
        if (eq.equipmentType === "PIPING_LINE" && eq.tag) {
          lineLabels.set(String(eq.id), String(eq.tag));
        }
      }
      this.performance = buildInspectorRanking({
        people: this.inspectors,
        inspections,
        assignments,
        fieldProgress,
        systems: allSystems,
        rdoEntries: allRdoItems,
        applied: this.applied,
        lineLabels
      });
      this.lastUpdatedAt = new Date();
    } catch (e) {
      console.error("[DASHBOARD] Falha ao carregar indicadores", e);
      if (generation !== this.dataGeneration) return;
      this.loadError = "Não foi possível carregar os indicadores. Tente atualizar novamente.";
    } finally {
      if (generation === this.dataGeneration) {
        this.isLoading = false;
        this.render();
      }
    }
  }

  // ----------------------------------------------------------------- render

  protected render() {
    this.destroyCharts();
    this.innerHTML = `<style>${this.styles()}</style>
      <div class="dash">
        ${this.renderHeader()}
        ${this.renderFilters()}
        ${this.isLoading ? this.renderSkeleton() : this.loadError ? this.renderPageError() : !this.data ? this.renderNoClient() : this.renderContent()}
      </div>`;
    this.bind();
    if (!this.isLoading && !this.loadError && this.data) this.paintCharts();
  }

  protected renderNoClient(): string {
    return `<div class="card empty" style="padding: 48px 24px; text-align: center;">
      <div class="empty-ico" style="margin-bottom: 12px;">${this.icon("alert")}</div>
      <div class="empty-title" style="font-size: 1.1rem; font-weight: 600; margin-bottom: 8px;">Nenhum cliente disponível</div>
      <div class="empty-desc" style="color: var(--color-text-secondary); max-width: 480px; margin: 0 auto;">Seu usuário não possui cliente autorizado.</div>
    </div>`;
  }

  protected renderHeader(): string {
    const stamp = this.lastUpdatedAt
      ? `${String(this.lastUpdatedAt.getDate()).padStart(2, "0")}/${String(this.lastUpdatedAt.getMonth() + 1).padStart(2, "0")}/${this.lastUpdatedAt.getFullYear()} ${String(this.lastUpdatedAt.getHours()).padStart(2, "0")}:${String(this.lastUpdatedAt.getMinutes()).padStart(2, "0")}`
      : "—";
    return `
      <header class="dash-head">
        <div>
          <nav class="crumb"><span class="crumb-root">INSPECT IA</span><span class="crumb-sep">›</span><span class="crumb-current">DASHBOARD</span></nav>
          <h1 class="dash-title">Painel de Indicadores e Avanços</h1>
          <p class="dash-sub">Visão operacional e gerencial das atividades de inspeção.</p>
        </div>
        <div class="head-actions">
          <div class="updated">
            ${this.icon("calendar")}
            <div>
              <div class="updated-label">Última atualização</div>
              <div class="updated-value" id="dash-updated">${stamp}</div>
            </div>
          </div>
          <button class="btn btn-ghost" id="btn-refresh" type="button" ${this.isLoading ? "disabled" : ""}>${this.icon("refresh")} Atualizar</button>
          ${this.canShare() ? `<button class="btn btn-primary" id="btn-share" type="button">${this.icon("share")} Compartilhar</button>` : ""}
        </div>
      </header>`;
  }

  /** Quem cria ou acompanha links do Painel (a permissão é conferida de novo no servidor). */
  protected canShare(): boolean {
    return this.can(PermissionCode.DashboardShareCreate) || this.can(PermissionCode.DashboardShareRead) || this.can(PermissionCode.DashboardShareManage);
  }

  /** Abre o painel de links com os filtros APLICADOS — é o recorte que o link carrega. */
  protected openSharePanel(): void {
    const panel = document.createElement("dashboard-share-panel") as HTMLElement & { open(init: unknown): void };
    document.body.appendChild(panel);
    panel.open({
      filters: { ...this.applied },
      projects: this.projects.map(p => ({ id: p.id, label: p.label })),
      unitAreas: this.unitAreas.map(u => ({ id: u.id, label: u.label, projectId: u.projectId })),
      inspectors: this.inspectors.map(i => ({ id: i.id, label: i.label }))
    });
  }

  protected renderFilters(): string {
    // PAINEL MULTICLIENTE: cascata Cliente → Projeto → Unidade (seções 5-7).
    // Projeto depende do filtro local de Cliente; Unidade depende do Projeto.
    const projectsForFilter = this.draft.clientId
      ? this.projects.filter(p => p.clientId === this.draft.clientId)
      : this.projects;
    const unitsForFilter = this.draft.projectId
      ? this.unitAreas.filter(u => u.projectId === this.draft.projectId)
      : this.draft.clientId
        ? this.unitAreas.filter(u => (u as any).clientId === this.draft.clientId)
        : this.unitAreas;
    return `
      ${this.ctxLoad.banner()}
      <section class="filters" aria-label="Filtros do painel">
        <label class="filter">
          <span class="filter-label">${this.icon("client")} Cliente</span>
          <select class="ctl" id="f-client">
            <option value="">Todos os Clientes</option>
            ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${this.draft.clientId === c.id ? "selected" : ""}>${this.esc(c.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">${this.icon("project")} Projeto</span>
          <select class="ctl" id="f-project">
            <option value="">Todos os Projetos</option>
            ${projectsForFilter.map(p => `<option value="${this.esc(p.id)}" ${this.draft.projectId === p.id ? "selected" : ""}>${this.esc(p.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">${this.icon("unit")} Unidade / Área</span>
          <select class="ctl" id="f-unit">
            <option value="">Todas as Unidades / Áreas</option>
            ${unitsForFilter.map(u => `<option value="${this.esc(u.id)}" ${this.draft.unitAreaId === u.id ? "selected" : ""}>${this.esc(u.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">${this.icon("person")} Inspetor</span>
          <select class="ctl" id="f-inspector">
            <option value="">${this.esc(this.ctxLoad.emptyLabel("Profissionais", "Nenhum Inspetor cadastrado", "Todos os Inspetores"))}</option>
            ${this.inspectors.map(i => `<option value="${this.esc(i.id)}" ${this.draft.inspectorProfessionalId === i.id ? "selected" : ""}>${this.esc(i.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter filter-period">
          <span class="filter-label">${this.icon("calendar")} Período (opcional)</span>
          <span class="period">
            <input class="ctl" type="date" id="f-from" value="${this.esc(this.draft.from || "")}" aria-label="Data inicial">
            <span class="period-sep">→</span>
            <input class="ctl" type="date" id="f-to" value="${this.esc(this.draft.to || "")}" aria-label="Data final">
          </span>
        </label>
        <div class="filter-actions">
          <button class="btn btn-primary" id="btn-apply" type="button">${this.icon("search")} Aplicar Filtros</button>
          <button class="btn btn-ghost" id="btn-clear" type="button">${this.icon("refresh")} Limpar Filtros</button>
        </div>
      </section>`;
  }

  protected renderSkeleton(): string {
    const card = `<div class="card sk"><div class="sk-line w40"></div><div class="sk-line w60 tall"></div><div class="sk-line w30"></div></div>`;
    return `
      <div class="grid grid-4">${card.repeat(4)}</div>
      <div class="grid grid-3">${card.repeat(3)}</div>
      <div class="grid grid-curve"><div class="card sk tallest"></div><div class="card sk tallest"></div></div>
      <div class="grid grid-3"><div class="card sk tall"></div><div class="card sk tall"></div><div class="card sk tall"></div></div>`;
  }

  protected renderPageError(): string {
    return `<div class="card empty"><div class="empty-ico">${this.icon("alert")}</div><div class="empty-title">Não foi possível carregar o painel</div><div class="empty-desc">${this.esc(this.loadError)}</div></div>`;
  }

  protected renderContent(): string {
    const d = this.data!;
    const deviationTone = d.deviation < 0 ? "neg" : d.deviation > 0 ? "pos" : "flat";
    const sign = d.deviation > 0 ? "+" : "";
    return `
      <div class="grid grid-4">
        ${this.kpi({ id: "kpi-assets", title: "ATIVOS NO ESCOPO", value: String(d.assets), sub: "Total Cadastrado", icon: "database", accent: "neutral", spark: "bars", route: this.can(PermissionCode.EquipmentRead) ? "/equipment" : null })}
        ${this.kpi({ id: "kpi-done", title: "INSPEÇÕES CONCLUÍDAS", value: String(d.completedInspections), sub: "Executadas", icon: "clipboard", accent: "done", spark: "bars", route: this.can(PermissionCode.InspectionRead) ? "/inspections" : null })}
        ${this.kpi({ id: "kpi-planned", title: "AVANÇO PLANEJADO", value: `${d.plannedProgress}%`, sub: "Curva S", icon: "target", accent: "neutral", spark: "line" })}
        ${this.kpi({ id: "kpi-real", title: "AVANÇO REAL", value: `${d.realProgress}%`, sub: "Medido", icon: "trend", accent: "done", spark: "line" })}
      </div>

      <div class="grid grid-3">
        ${this.kpi({ id: "kpi-dev", title: "DESVIO", value: `${sign}${d.deviation}%`, sub: "Real vs Planejado", icon: "scale", accent: deviationTone === "neg" ? "warn" : deviationTone === "pos" ? "done" : "neutral", spark: "line" })}
        ${this.kpi({ id: "kpi-reports", title: "RELATÓRIOS EMITIDOS", value: String(d.issuedReports), sub: "Emitidos/Laudos", icon: "document", accent: "neutral", spark: "bars", route: this.can(PermissionCode.ReportRead) ? "/reports/integrity" : null })}
        ${this.kpi({ id: "kpi-recs", title: "RECOMENDAÇÕES ABERTAS", value: String(d.openRecommendations), sub: "Pendentes", icon: "alert", accent: "alert", spark: "bars", route: this.can(PermissionCode.RecommendationRead) ? "/integrity/recommendations" : null })}
      </div>

      <div class="grid grid-curve">
        <section class="card">
          <div class="card-head"><h2 class="card-title">Curva S - Avanço das Inspeções</h2></div>
          ${d.curve.hasData
            ? `<div class="chart-box"><canvas id="curve-chart"></canvas></div>
               <div class="legend">
                 <span class="lg"><i class="sw sw-bar"></i> Inspeções Executadas</span>
                 <span class="lg"><i class="sw sw-planned"></i> Planejado</span>
                 <span class="lg"><i class="sw sw-real"></i> Real</span>
               </div>`
            : this.empty("Nenhum dado disponível", "Não há inspeções com data no recorte selecionado para compor a Curva S.")}
        </section>

        <section class="card">
          <div class="card-head"><h2 class="card-title">Status das Inspeções</h2></div>
          ${d.status.hasData
            ? `<div class="status-body">
                 <div class="donut-box">
                   <canvas id="status-chart"></canvas>
                   <div class="donut-center"><strong>${d.status.total}</strong><span>Ativos</span></div>
                 </div>
                 <ul class="status-list">
                   ${d.status.slices.map(s => `<li><span class="dot dot-${s.key.toLowerCase()}"></span><span class="s-label">${this.esc(s.label)}</span><span class="s-count">${s.count}</span><span class="s-pct">${s.percent}%</span></li>`).join("")}
                 </ul>
               </div>`
            : this.empty("Nenhum dado disponível", "Cadastre ativos para acompanhar o status das inspeções.")}
        </section>
      </div>

      ${this.inspectorRanking()}

      <div class="grid grid-3">
        ${this.barsCard("Avanços por unidade", "unit", d.unitProgress.bars, d.unitProgress.hasData, "Nenhuma Unidade / Área com inspeções no recorte selecionado.")}
        ${this.barsCard("Avanço por sistemas", "system", d.systemProgress.bars, d.systemProgress.hasData, "Nenhum Sistema informado no cadastro dos ativos do recorte.")}
        ${this.shortcuts()}
      </div>`;
  }

  /**
   * RANKING DE AVANÇO DAS INSPEÇÕES POR INSPETOR.
   *
   * Cada linha abre o detalhamento por Sistema — de onde vem o número da
   * pessoa. Quem não tem escopo atribuído aparece com o motivo, não com 0%:
   * zero seria uma afirmação sobre o trabalho dela, e o que falta é
   * cadastro.
   */
  protected inspectorRanking(): string {
    const report = this.performance;
    if (!report) return "";
    const selected = this.applied.inspectorProfessionalId || "";
    const rows = report.inspectors;

    // Leitura negada ou falha NÃO é "ninguém tem escopo": sem os vínculos o
    // ranking simplesmente não pode ser montado, e é isso que o card diz.
    const load = this.assignmentsLoad;
    const body = load && load.status !== "ok"
      ? this.empty("Ranking indisponível", load.message)
      : rows.length === 0
      ? this.empty("Nenhum Inspetor no recorte", "Não há Profissional classificado como Inspetor nem inspeção atribuída no recorte selecionado.")
      : `<ul class="rank">
          ${rows.map((person, index) => {
            const open = this.expandedInspector === person.professionalId;
            const detail = person.systems.length > 0
              ? `${this.rateSummary(person.dailyRate)}
                 <table class="rank-detail">
                   <thead><tr><th>Sistema</th><th>Linhas</th><th>Concluídas</th><th>Pendentes</th><th>Fora de operação</th><th>Avanço</th><th>Linhas/dia</th></tr></thead>
                   <tbody>
                     ${person.systems.map(s => {
                       // REGRA V3: Renderizar TAGs individuais SOMENTE para o agrupador
                       // LINHAS OFF-SITE/TUBOVIA (__OFFSITE_TUBOVIA__). Sistemas reais mostram
                       // apenas totais agregados; TAGs de linhas de Sistema são PROIBIDAS aqui.
                       const isOffsiteGroup = s.systemId === "__OFFSITE_TUBOVIA__";
                       const hasTags = isOffsiteGroup && Array.isArray(s.lineTags) && s.lineTags.length > 0;
                       const tagRows = hasTags
                         ? `<tr class="rank-detail-tags"><td colspan="7"><ul class="offsite-tag-list">${s.lineTags!.map(lt =>
                             `<li><span class="offsite-tag">${this.esc(lt.tag)}</span>${lt.progressPercent != null ? ` <span class="offsite-tag-pct">${lt.progressPercent}%</span>` : ""}${lt.outOfOperation ? ` <span class="tag-off-op" title="Fora de operação">FO</span>` : ""}</li>`
                           ).join("")}</ul></td></tr>`
                         : "";
                       return `<tr>
                       <td>${this.esc(s.label)}${s.shared ? ` <span class="tag-shared" title="Sistema acompanhado também por outro Inspetor">COMPARTILHADO</span>` : ""}</td>
                       <td>${s.lines}</td><td>${s.completed}</td><td>${s.pending}</td><td>${s.outOfOperation}</td><td>${s.progressPercent}%</td>
                       <td title="Por Sistema o denominador é sempre o dia com conclusão: o RDO aponta o dia da pessoa, não o Sistema em que ela trabalhou.">${s.dailyRate.hasData ? this.rateText(s.dailyRate.linesPerWorkedDay) : "—"}</td>
                     </tr>${tagRows}`;
                     }).join("")}
                   </tbody>
                 </table>`
              : `<p class="rank-note">Sem Sistema de Tubulação atribuído. Os números acima vêm das inspeções sob responsabilidade desta pessoa.</p>`;
            return `
              <li class="rank-row ${person.professionalId === selected ? "is-selected" : ""} ${person.hasScope ? "" : "no-scope"}">
                <button class="rank-head" type="button" data-inspector="${this.esc(person.professionalId)}" aria-expanded="${open}">
                  <span class="rank-pos">${person.hasScope ? index + 1 : "—"}</span>
                  <span class="rank-name">${this.esc(person.name)}</span>
                  ${person.hasScope
                    ? `<span class="rank-rate">
                         ${person.dailyRate.hasData
                           ? `<strong>${this.rateText(person.dailyRate.linesPerWorkedDay)}</strong><small>${person.dailyRate.daysSource === WorkedDaysSource.Rdo ? "linhas / RDO" : "linhas/dia (estimado)"}</small>`
                           : `<strong class="rank-nodate">—</strong><small>sem conclusão datada</small>`}
                       </span>
                       <span class="b-track" title="Avanço médio das linhas operacionais"><span class="b-fill" style="width:${person.progressPercent}%"></span></span>
                       <span class="rank-pct">${person.progressPercent}%</span>
                       <span class="rank-meta">${person.completedLines}/${person.operationalLines} linhas · ${person.assignedSystems} sistema(s)${person.sharedLines > 0 ? ` · ${person.sharedLines} compartilhada(s)` : ""}</span>`
                    : `<span class="rank-unavailable">${this.esc(person.unavailableReason ?? "")}</span>`}
                  <span class="rank-caret">${open ? "▾" : "▸"}</span>
                </button>
                ${open ? `<div class="rank-body">${detail}</div>` : ""}
              </li>`;
          }).join("")}
         </ul>
         ${report.overlapping
           ? `<p class="rank-note">Há Sistemas com mais de um Inspetor: a mesma Linha entra no escopo de cada um deles, então a soma das linhas por pessoa passa das ${report.distinctLinesInScope} linhas físicas do recorte.</p>`
           : ""}`;

    return `
      <section class="card">
        <div class="card-head">
          <h2 class="card-title">Avanço das inspeções por Inspetor</h2>
          <span class="card-hint">Linhas concluídas ÷ RDOs do período · clique para ver os Sistemas</span>
        </div>
        ${body}
      </section>`;
  }

  protected kpi(o: { id: string; title: string; value: string; sub: string; icon: string; accent: string; spark: string; route?: string | null }): string {
    const clickable = !!o.route;
    return `
      <article class="card kpi accent-${o.accent} ${clickable ? "is-clickable" : ""}" id="${o.id}" ${clickable ? `data-route="${this.esc(o.route!)}" role="link" tabindex="0"` : ""}>
        <span class="kpi-rail"></span>
        <div class="kpi-ico">${this.icon(o.icon)}</div>
        <div class="kpi-body">
          <div class="kpi-title">${this.esc(o.title)}</div>
          <div class="kpi-value">${this.esc(o.value)}</div>
          <div class="kpi-sub">${this.esc(o.sub)}</div>
        </div>
        <div class="kpi-spark">${o.spark === "bars" ? this.sparkBars() : this.sparkLine()}</div>
      </article>`;
  }

  protected barsCard(title: string, kind: string, bars: ReadonlyArray<{ id: string; label: string; percent: number; completed: number; total: number }>, hasData: boolean, emptyDesc: string): string {
    const LIMIT = 5;
    const visible = kind === "unit" && !this.showAllUnits ? bars.slice(0, LIMIT) : bars;
    const more = kind === "unit" && bars.length > LIMIT;
    return `
      <section class="card">
        <div class="card-head"><h2 class="card-title">${this.esc(title)}</h2></div>
        ${hasData
          ? `<ul class="bars">
              ${visible.map(b => `
                <li title="${this.esc(b.label)} — ${b.completed} de ${b.total} inspeções concluídas (${b.percent}%)">
                  <span class="b-label">${this.esc(b.label)}</span>
                  <span class="b-track"><span class="b-fill" style="width:${b.percent}%"></span></span>
                  <span class="b-pct">${b.percent}%</span>
                </li>`).join("")}
             </ul>
             ${more ? `<button class="link-more" id="btn-more-units" type="button">${this.showAllUnits ? "VER MENOS" : "VER TODAS"}</button>` : ""}`
          : this.empty("Nenhum dado disponível", emptyDesc)}
      </section>`;
  }

  protected shortcuts(): string {
    const all = [
      { title: "Ativos no Escopo", sub: "Ver lista de equipamentos", icon: "database", route: "/equipment", permission: PermissionCode.EquipmentRead },
      { title: "Inspeções em Andamento", sub: "Acompanhar atividades", icon: "clipboard", route: "/inspections", permission: PermissionCode.InspectionRead },
      { title: "Recomendações Pendentes", sub: "Ver recomendações abertas", icon: "alert", route: "/integrity/recommendations", permission: PermissionCode.RecommendationRead },
      { title: "Relatórios Emitidos", sub: "Acessar relatórios", icon: "document", route: "/reports/integrity", permission: PermissionCode.ReportRead }
    ].filter(s => this.can(s.permission));

    return `
      <section class="card">
        <div class="card-head"><h2 class="card-title">${this.icon("bolt")} Atalhos Rápidos</h2></div>
        ${all.length > 0
          ? `<div class="shortcuts">
              ${all.map(s => `
                <button class="shortcut" type="button" data-route="${this.esc(s.route)}">
                  <span class="sc-ico">${this.icon(s.icon)}</span>
                  <span class="sc-text"><strong>${this.esc(s.title)}</strong><small>${this.esc(s.sub)}</small></span>
                  <span class="sc-arrow">→</span>
                </button>`).join("")}
             </div>`
          : this.empty("Nenhum atalho disponível", "Configure seus atalhos no painel de preferências.")}
      </section>`;
  }

  /** "2,5" — vírgula decimal, e sem casa quando o número é inteiro. */
  protected rateText(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(".", ",");
  }

  protected dayText(day: string | null): string {
    if (!day) return "—";
    const [year, month, date] = day.split("-");
    return year && month && date ? `${date}/${month}/${year}` : day;
  }

  /**
   * As DUAS médias, lado a lado. Só a de dia trabalhado enganaria: quem
   * concluiu 8 Linhas num único dia de um mês apareceria com o mesmo ritmo
   * de quem concluiu 8 por dia o mês inteiro. A de dia corrido é a que serve
   * para prazo.
   */
  protected rateSummary(rate: InspectorDailyRate): string {
    const pending = rate.completedWithoutDate > 0
      ? `<p class="rank-note">${rate.completedWithoutDate} linha(s) concluída(s) sem data de conclusão registrada — ficam fora da média, não entram como zero.</p>`
      : "";
    if (!rate.hasData) {
      return `<p class="rank-note">Nenhuma linha concluída com data no recorte selecionado: sem isso não há média por dia.</p>${pending}`;
    }

    const fromRdo = rate.daysSource === WorkedDaysSource.Rdo;
    // A origem do denominador muda o significado do número. O derivado só
    // enxerga dias produtivos e por isso superestima — dizer isso é o que
    // impede que um 6,7 derivado seja lido como fato.
    //
    // RDOs ≠ dias distintos quando a pessoa aponta mais de um no mesmo dia
    // (Frentes ou turnos diferentes). A conta é a pedida — por RDO — e a
    // divergência é mostrada em vez de ser arredondada em silêncio.
    const multiplePerDay = fromRdo && rate.reportedEntries > rate.reportedDays;
    const origin = fromRdo
      ? `<p class="rank-note">Média = <strong>${rate.completedWithDate} linha(s) concluída(s) ÷ ${rate.reportedEntries} RDO(s)</strong> criados no período.${rate.daysWithoutCompletion > 0 ? ` ${rate.daysWithoutCompletion} dia(s) em campo sem nenhuma linha concluída entram no denominador.` : ""}${multiplePerDay ? ` Atenção: são ${rate.reportedEntries} RDOs em ${rate.reportedDays} dia(s) — há mais de um apontamento no mesmo dia.` : ""}</p>`
      : `<p class="rank-note rank-warn">Sem RDO no recorte: o denominador foi <strong>derivado dos dias com conclusão</strong>. Essa média <strong>superestima o ritmo</strong>, porque só enxerga os dias produtivos. Preencha o RDO para o número ficar real.</p>`;

    return `
      <div class="rate-grid">
        <div><span class="rate-num">${this.rateText(rate.linesPerWorkedDay)}</span><span class="rate-cap">linhas / ${fromRdo ? "RDO" : "dia (estimado)"}</span></div>
        <div><span class="rate-num">${this.rateText(rate.linesPerElapsedDay)}</span><span class="rate-cap">linhas / dia corrido</span></div>
        <div><span class="rate-num">${rate.completedWithDate}</span><span class="rate-cap">linhas concluídas</span></div>
        <div><span class="rate-num">${rate.daysWorked}</span><span class="rate-cap">${fromRdo ? "RDOs no período" : "dias com conclusão"}</span></div>
        ${fromRdo && multiplePerDay ? `<div><span class="rate-num">${rate.reportedDays}</span><span class="rate-cap">dias distintos</span></div>` : ""}
        ${fromRdo && rate.hoursWorked > 0 ? `<div><span class="rate-num">${this.rateText(rate.hoursWorked)}</span><span class="rate-cap">horas apontadas</span></div>` : ""}
        <div><span class="rate-num">${rate.elapsedDays}</span><span class="rate-cap">dias corridos</span></div>
        <div><span class="rate-num rate-span">${this.dayText(rate.firstDay)} → ${this.dayText(rate.lastDay)}</span><span class="rate-cap">período medido</span></div>
      </div>${origin}${pending}`;
  }

  protected empty(title: string, desc: string): string {
    return `<div class="empty"><div class="empty-ico">${this.icon("empty")}</div><div class="empty-title">${this.esc(title)}</div><div class="empty-desc">${this.esc(desc)}</div></div>`;
  }

  // ------------------------------------------------------------------ charts

  protected paintCharts() {
    const d = this.data;
    if (!d) return;

    const curveCanvas = this.querySelector("#curve-chart") as HTMLCanvasElement | null;
    if (curveCanvas && d.curve.hasData) {
      this.curveChart = new Chart(curveCanvas.getContext("2d")!, {
        data: {
          labels: d.curve.points.map(p => p.label),
          datasets: [
            { type: "bar", label: "Inspeções Executadas", data: d.curve.points.map(p => p.executed), backgroundColor: INK.bars, borderColor: INK.barsBorder, borderWidth: 1, yAxisID: "yCount", order: 3, barPercentage: 0.6, categoryPercentage: 0.7 },
            { type: "line", label: "Planejado", data: d.curve.points.map(p => p.planned), borderColor: INK.planned, backgroundColor: INK.planned, borderDash: [5, 4], borderWidth: 2, pointRadius: 2, tension: 0.25, yAxisID: "y", order: 1 },
            { type: "line", label: "Real", data: d.curve.points.map(p => p.real), borderColor: INK.real, backgroundColor: INK.real, borderWidth: 2, pointRadius: 2, tension: 0.25, yAxisID: "y", order: 0 }
          ]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "#12161A", borderColor: "rgba(255,255,255,0.12)", borderWidth: 1, titleColor: INK.text, bodyColor: INK.muted, padding: 10, displayColors: true,
              callbacks: { label: (item: any) => (item.dataset.yAxisID === "yCount" ? `${item.dataset.label}: ${item.parsed.y}` : `${item.dataset.label}: ${item.parsed.y}%`) }
            }
          },
          scales: {
            y: { position: "left", beginAtZero: true, max: 100, ticks: { color: INK.muted, stepSize: 20, callback: (v: any) => `${v}%` }, grid: { color: INK.grid } },
            yCount: { position: "right", beginAtZero: true, ticks: { color: INK.muted, precision: 0 }, grid: { display: false } },
            x: { ticks: { color: INK.muted }, grid: { display: false } }
          }
        }
      });
    }

    const statusCanvas = this.querySelector("#status-chart") as HTMLCanvasElement | null;
    if (statusCanvas && d.status.hasData) {
      this.statusChart = new Chart(statusCanvas.getContext("2d")!, {
        type: "doughnut",
        data: {
          labels: d.status.slices.map(s => s.label),
          datasets: [{ data: d.status.slices.map(s => s.count), backgroundColor: [INK.done, INK.pending, INK.out], borderColor: "#10151A", borderWidth: 2, hoverOffset: 4 }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: "72%",
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "#12161A", borderColor: "rgba(255,255,255,0.12)", borderWidth: 1, titleColor: INK.text, bodyColor: INK.muted, padding: 10,
              callbacks: { label: (item: any) => `${item.label}: ${item.parsed} (${d.status.slices[item.dataIndex]?.percent ?? 0}%)` }
            }
          }
        }
      });
    }
  }

  // ------------------------------------------------------------------ events

  protected bind() {
    this.querySelector("#btn-refresh")?.addEventListener("click", () => { void this.loadData(); });
    this.querySelector("#btn-share")?.addEventListener("click", () => this.openSharePanel());

    const project = this.querySelector("#f-project") as HTMLSelectElement | null;
    project?.addEventListener("change", () => {
      this.draft.projectId = project.value || null;
      // Unidade que não pertence ao novo Projeto é limpa automaticamente.
      if (this.draft.unitAreaId && !this.unitAreas.some(u => u.id === this.draft.unitAreaId && (!this.draft.projectId || u.projectId === this.draft.projectId))) {
        this.draft.unitAreaId = null;
      }
      this.render();
    });
    (this.querySelector("#f-unit") as HTMLSelectElement | null)?.addEventListener("change", e => { this.draft.unitAreaId = (e.target as HTMLSelectElement).value || null; });
    (this.querySelector("#f-inspector") as HTMLSelectElement | null)?.addEventListener("change", e => { this.draft.inspectorProfessionalId = (e.target as HTMLSelectElement).value || null; });
    (this.querySelector("#f-from") as HTMLInputElement | null)?.addEventListener("change", e => { this.draft.from = (e.target as HTMLInputElement).value || null; });
    (this.querySelector("#f-to") as HTMLInputElement | null)?.addEventListener("change", e => { this.draft.to = (e.target as HTMLInputElement).value || null; });

    this.querySelector("#btn-apply")?.addEventListener("click", () => {
      this.applied = { ...this.draft };
      // [ONLINE-TRACE][DASHBOARD-UI] Diagnóstico temporário seguro — captura
      // filtros reais da UI antes de chamar loadData() para comparar com RPC.
      console.log("[ONLINE-TRACE][DASHBOARD-UI]", JSON.stringify({
        action: "APPLY_FILTERS",
        projectId: this.applied.projectId ?? null,
        unitAreaId: this.applied.unitAreaId ?? null,
        inspectorProfessionalId: this.applied.inspectorProfessionalId ?? null,
        dateFrom: this.applied.from ?? null,
        dateTo: this.applied.to ?? null,
        activeClientId: (window as any).contextStore?.get?.()?.activeClientId ?? null
      }));
      void this.loadData();
    });
    this.querySelector("#btn-clear")?.addEventListener("click", () => {
      this.draft = {}; this.applied = {}; this.showAllUnits = false; this.expandedInspector = null;
      // [ONLINE-TRACE][DASHBOARD-UI] Diagnóstico temporário seguro — captura
      // limpeza de filtros para confirmar reset completo.
      console.log("[ONLINE-TRACE][DASHBOARD-UI]", JSON.stringify({
        action: "CLEAR_FILTERS",
        projectId: null,
        unitAreaId: null,
        inspectorProfessionalId: null,
        dateFrom: null,
        dateTo: null,
        activeClientId: (window as any).contextStore?.get?.()?.activeClientId ?? null
      }));
      void this.loadData();
    });
    // REGRESSÃO V3-A1/A2: troca de Cliente/Projeto deve aplicar o filtro
    // imediatamente (copiar draft → applied + loadData), não apenas renderizar.
    // O método applyDraftFilters() centraliza essa lógica para evitar duplicidade.
    (this.querySelector("#f-client") as HTMLSelectElement | null)?.addEventListener("change", e => {
      const newClientId = (e.target as HTMLSelectElement).value || null;
      this.draft.clientId = newClientId;
      // Cascata: se o Projeto atual não pertence ao novo Cliente, limpa.
      if (this.draft.projectId) {
        const proj = this.projects.find(p => p.id === this.draft.projectId);
        if (!newClientId || (proj && proj.clientId !== newClientId)) {
          this.draft.projectId = null;
          this.draft.unitAreaId = null;
        }
      }
      // Se Unidade não pertence ao novo contexto, limpa.
      if (this.draft.unitAreaId && !this.unitAreas.some(u =>
        u.id === this.draft.unitAreaId &&
        (!this.draft.projectId || u.projectId === this.draft.projectId) &&
        (!newClientId || (u as any).clientId === newClientId)
      )) {
        this.draft.unitAreaId = null;
      }
      void this.applyDraftFilters();
    });
    (this.querySelector("#f-project") as HTMLSelectElement | null)?.addEventListener("change", e => {
      this.draft.projectId = (e.target as HTMLSelectElement).value || null;
      // Preenche clientId do Projeto selecionado para manter coerência.
      if (this.draft.projectId) {
        const proj = this.projects.find(p => p.id === this.draft.projectId);
        if (proj?.clientId) this.draft.clientId = proj.clientId;
      }
      // Limpa Unidade se não pertencer ao Projeto selecionado.
      if (this.draft.unitAreaId && !this.unitAreas.some(u =>
        u.id === this.draft.unitAreaId &&
        (!this.draft.projectId || u.projectId === this.draft.projectId)
      )) {
        this.draft.unitAreaId = null;
      }
      void this.applyDraftFilters();
    });
    this.querySelector("#btn-more-units")?.addEventListener("click", () => { this.showAllUnits = !this.showAllUnits; this.render(); });

    // Drill-down do ranking: abre/fecha sem recarregar — o dado já está na
    // tela, e recarregar perderia o recorte aplicado.
    this.querySelectorAll<HTMLElement>("[data-inspector]").forEach(el => {
      el.addEventListener("click", () => {
        const id = el.getAttribute("data-inspector") || "";
        this.expandedInspector = this.expandedInspector === id ? null : id;
        this.render();
      });
    });

    this.querySelectorAll<HTMLElement>("[data-route]").forEach(el => {
      const go = () => { const route = el.getAttribute("data-route"); if (route) window.location.href = route; };
      el.addEventListener("click", go);
      el.addEventListener("keydown", ev => { if ((ev as KeyboardEvent).key === "Enter") go(); });
    });
  }

  // ------------------------------------------------------------------- utils

  protected esc(value: string): string {
    return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  protected sparkBars(): string {
    return `<svg viewBox="0 0 48 24" aria-hidden="true"><rect x="2" y="14" width="6" height="10" rx="1"/><rect x="12" y="9" width="6" height="15" rx="1"/><rect x="22" y="12" width="6" height="12" rx="1"/><rect x="32" y="5" width="6" height="19" rx="1"/><rect x="42" y="10" width="4" height="14" rx="1"/></svg>`;
  }
  protected sparkLine(): string {
    return `<svg viewBox="0 0 48 24" fill="none" aria-hidden="true"><path d="M2 18 L12 14 L20 16 L30 8 L38 10 L46 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  protected icon(name: string): string {
    const paths: Record<string, string> = {
      calendar: `<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>`,
      refresh: `<path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/>`,
      project: `<path d="M3 7h6l2 3h10v9a2 2 0 0 1-2 2H3z"/><path d="M3 7V5a2 2 0 0 1 2-2h4"/>`,
      unit: `<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>`,
      search: `<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>`,
      database: `<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.7 4 3 9 3s9-1.3 9-3V5"/><path d="M3 12c0 1.7 4 3 9 3s9-1.3 9-3"/>`,
      clipboard: `<rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="m9 14 2 2 4-4"/>`,
      target: `<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/>`,
      trend: `<path d="M3 17l6-6 4 4 7-7"/><path d="M14 8h6v6"/>`,
      scale: `<path d="M12 3v18"/><path d="M5 7h14"/><path d="m5 7-3 6h6z"/><path d="m19 7-3 6h6z"/>`,
      document: `<path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z"/><path d="M14 2v5h5"/><path d="M9 13h6M9 17h6"/>`,
      alert: `<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/>`,
      bolt: `<path d="M13 2 3 14h8l-1 8 10-12h-8z"/>`,
      person: `<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>`,
      share: `<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4"/>`,
      lock: `<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>`,
      eye: `<path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>`,
      empty: `<path d="M3 3v18h18"/><path d="M7 15l4-4 3 3 5-6"/>`
    };
    return `<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[name] || paths["empty"]}</svg>`;
  }

  protected styles(): string {
    return `
      .dash { display: flex; flex-direction: column; gap: 16px; }

      .dash-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
      .crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; }
      .crumb-root { color: var(--color-text-secondary, #9BA4AE); }
      .crumb-sep { color: var(--ia-fg-muted, rgba(255,255,255,0.45)); }
      .crumb-current { color: var(--ia-fg, #C8D2DC); font-weight: 600; }
      .dash-title { margin: 6px 0 2px; font-size: clamp(1.4rem, 2.2vw, 1.9rem); font-weight: 600; color: var(--color-text-primary, #E7EAED); letter-spacing: -0.01em; }
      .dash-sub { margin: 0; color: var(--color-text-muted, #8A939D); font-size: 0.875rem; }

      .head-actions { display: flex; align-items: center; gap: 10px; }
      .updated { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border: 1px solid var(--color-border-default, rgba(255,255,255,0.08)); border-radius: 8px; background: var(--color-surface-primary, #12161A); }
      .updated .ico { width: 18px; height: 18px; color: var(--ia-fg-muted, #8A939D); }
      .updated-label { font-size: 0.65rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .updated-value { font-size: 0.8rem; color: var(--ia-fg, #D6DCE2); font-variant-numeric: tabular-nums; }

      .btn { display: inline-flex; align-items: center; gap: 7px; padding: 9px 14px; border-radius: 8px; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; cursor: pointer; border: 1px solid transparent; transition: background .15s, border-color .15s; }
      .btn .ico { width: 15px; height: 15px; }
      .btn:disabled { opacity: .55; cursor: default; }
      .btn-primary { background: var(--ia-bg-raised, #2B3A47); color: var(--ia-fg, #E7EAED); border-color: var(--ia-line, #3A4C5C); }
      .btn-primary:hover:not(:disabled) { background: var(--ia-bg-raised, #334554); }
      .btn-ghost { background: var(--ia-bg-panel, #171C21); color: var(--ia-fg-soft, #C3CAD1); border-color: var(--ia-line, rgba(255,255,255,0.10)); }
      .btn-ghost:hover:not(:disabled) { background: var(--ia-bg-panel, #1D2329); }

      /*
       * BARRA DE FILTROS — flex com quebra, nunca grade de colunas fixas.
       * Causa da sobreposição antiga: a coluna do período (1.1fr) encolhia
       * abaixo da largura mínima REAL dos dois <input type="date"> (~158px
       * cada), a grade interna deles não encolhia junto e as datas vazavam
       * para baixo do botão APLICAR FILTROS. Aqui cada item declara a sua
       * largura mínima verdadeira e, quando não cabe, desce de linha.
       */
      .filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; padding: 14px; border: 1px solid var(--color-border-default, rgba(255,255,255,0.08)); border-radius: 10px; background: var(--color-surface-primary, #12161A); }
      .filter { display: flex; flex-direction: column; gap: 6px; flex: 1 1 190px; min-width: min(100%, 190px); }
      .filter-period { flex: 2 1 360px; min-width: min(100%, 360px); }
      .filter-label { display: inline-flex; align-items: center; gap: 6px; font-size: 0.68rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .filter-label .ico { width: 14px; height: 14px; }
      .ctl { width: 100%; padding: 9px 10px; border-radius: 7px; background: var(--ia-bg-inset, #0E1216); border: 1px solid var(--ia-line, rgba(255,255,255,0.10)); color: var(--ia-fg, #DCE2E8); font-size: 0.82rem; font-family: inherit; }
      .ctl:focus { outline: none; border-color: var(--ia-line, #3A4C5C); }
      .period { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
      /* 14em da fonte do próprio campo = a largura em que dd/mm/aaaa + ícone cabem sem corte. */
      .period .ctl { flex: 1 1 14em; width: auto; min-width: 14em; }
      .period-sep { flex: 0 0 auto; color: var(--ia-fg-muted, #8A939D); }
      .filter-actions { display: flex; flex-wrap: wrap; gap: 8px; flex: 0 0 auto; max-width: 100%; margin-left: auto; }
      .filter-actions .btn { white-space: nowrap; }

      .grid { display: grid; gap: 14px; }
      .grid-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
      .grid-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .grid-curve { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); }

      .card { position: relative; background: var(--color-surface-primary, #12161A); border: 1px solid var(--color-border-default, rgba(255,255,255,0.08)); border-radius: 10px; padding: 16px; box-shadow: none; min-width: 0; }
      .card-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 12px; }
      .card-title { margin: 0; display: inline-flex; align-items: center; gap: 8px; font-size: 0.95rem; font-weight: 600; color: var(--ia-fg, #DCE2E8); }
      .card-title .ico { width: 16px; height: 16px; color: var(--ia-fg-muted, #8A939D); }

      .kpi { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 12px; overflow: hidden; min-height: 104px; }
      .kpi-rail { position: absolute; inset: 0 auto 0 0; width: 3px; background: rgba(255,255,255,0.16); border-radius: 10px 0 0 10px; }
      .accent-done .kpi-rail { background: #6E9E7E; }
      .accent-warn .kpi-rail { background: #A76F6F; }
      .accent-alert .kpi-rail { background: #B29455; }
      .kpi-ico { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 9px; background: rgba(255,255,255,0.05); border: 1px solid var(--ia-line, rgba(255,255,255,0.07)); color: var(--ia-fg-soft, #A9B2BB); }
      .kpi-ico .ico { width: 19px; height: 19px; }
      .kpi-title { font-size: 0.66rem; letter-spacing: 0.09em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .kpi-value { font-size: clamp(1.5rem, 2.4vw, 2rem); font-weight: 600; color: var(--ia-fg, #EDF1F4); line-height: 1.15; font-variant-numeric: tabular-nums; }
      .kpi-sub { font-size: 0.72rem; color: var(--ia-fg-muted, #8A939D); }
      .kpi-spark { width: 48px; color: var(--ia-fg-muted, rgba(255,255,255,0.22)); align-self: center; }
      .kpi-spark svg { width: 48px; height: 24px; fill: currentColor; }
      .accent-done .kpi-spark { color: rgba(110,158,126,0.5); }
      .accent-warn .kpi-spark { color: rgba(167,111,111,0.5); }
      .accent-alert .kpi-spark { color: rgba(178,148,85,0.5); }
      .is-clickable { cursor: pointer; }
      .is-clickable:hover { border-color: var(--ia-line, rgba(255,255,255,0.18)); background: var(--ia-bg-panel, #151A20); }
      .is-clickable:focus-visible { outline: 2px solid #3A4C5C; outline-offset: 2px; }

      .chart-box { height: 260px; }
      .legend { display: flex; flex-wrap: wrap; gap: 16px; justify-content: center; margin-top: 10px; font-size: 0.72rem; color: var(--ia-fg-muted, #8A939D); }
      .lg { display: inline-flex; align-items: center; gap: 7px; }
      .sw { display: inline-block; width: 14px; height: 3px; border-radius: 2px; }
      .sw-bar { height: 10px; width: 10px; background: rgba(255,255,255,0.22); border: 1px solid var(--ia-line, rgba(255,255,255,0.3)); border-radius: 2px; }
      .sw-planned { background: #8C99A6; }
      .sw-real { background: #E7EAED; }

      .status-body { display: grid; grid-template-columns: auto 1fr; gap: 16px; align-items: center; }
      .donut-box { position: relative; width: 150px; height: 150px; }
      .donut-center { position: absolute; inset: 0; display: grid; place-content: center; text-align: center; pointer-events: none; }
      .donut-center strong { display: block; font-size: 1.5rem; font-weight: 600; color: var(--ia-fg, #EDF1F4); font-variant-numeric: tabular-nums; }
      .donut-center span { font-size: 0.68rem; color: var(--ia-fg-muted, #8A939D); letter-spacing: 0.06em; text-transform: uppercase; }
      .status-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
      .status-list li { display: grid; grid-template-columns: 10px 1fr auto auto; align-items: center; gap: 10px; font-size: 0.8rem; color: var(--ia-fg-soft, #C3CAD1); }
      .dot { width: 9px; height: 9px; border-radius: 50%; }
      .dot-concluidas { background: #6E9E7E; }
      .dot-pendentes { background: #B29455; }
      .dot-fora_de_operacao { background: #A76F6F; }
      .s-count { color: var(--ia-fg, #EDF1F4); font-variant-numeric: tabular-nums; }
      .s-pct { color: var(--ia-fg-muted, #8A939D); width: 42px; text-align: right; font-variant-numeric: tabular-nums; }

      .bars { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 11px; }
      .bars li { display: grid; grid-template-columns: minmax(80px, 34%) 1fr 42px; align-items: center; gap: 10px; font-size: 0.78rem; color: var(--ia-fg-soft, #C3CAD1); }
      .b-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .b-track { height: 8px; border-radius: 4px; background: rgba(255,255,255,0.06); overflow: hidden; }
      .b-fill { display: block; height: 100%; border-radius: 4px; background: linear-gradient(90deg, #5B6B7A, #8C99A6); }

      /* RANKING POR INSPETOR */
      .card-hint { font-size: 0.7rem; color: var(--ia-fg-muted, #8A939D); letter-spacing: 0.04em; text-transform: uppercase; }
      .rank { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
      .rank-row { border: 1px solid var(--ia-line, rgba(255,255,255,0.07)); border-radius: 8px; background: rgba(255,255,255,0.015); }
      .rank-row.is-selected { border-color: var(--ia-line, #3A4C5C); background: rgba(90,120,150,0.10); }
      .rank-head { width: 100%; display: grid; grid-template-columns: 34px minmax(120px, 1.2fr) 86px minmax(80px, 1.6fr) 50px minmax(140px, 1.3fr) 22px; align-items: center; gap: 12px; padding: 11px 12px; background: none; border: 0; cursor: pointer; text-align: left; color: inherit; }
      .rank-head:hover { background: rgba(255,255,255,0.03); border-radius: 8px; }
      .rank-pos { font-variant-numeric: tabular-nums; font-size: 0.8rem; color: var(--ia-fg-muted, #8A939D); text-align: center; }
      .rank-name { font-size: 0.85rem; color: var(--ia-fg, #D6DCE2); font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .rank-pct { font-variant-numeric: tabular-nums; font-size: 0.85rem; color: var(--ia-fg, #E7EAED); text-align: right; }
      .rank-meta { font-size: 0.7rem; color: var(--ia-fg-muted, #8A939D); }
      .rank-caret { color: var(--ia-fg-muted, #8A939D); font-size: 0.75rem; text-align: right; }
      .rank-row.no-scope .rank-name { color: var(--ia-fg-soft, #9BA4AE); }
      .rank-unavailable { grid-column: 3 / 7; font-size: 0.7rem; letter-spacing: 0.04em; color: var(--ia-warning-fg, #B29455); text-transform: uppercase; }
      .rank-rate { display: flex; flex-direction: column; line-height: 1.15; }
      .rank-rate strong { font-size: 1.05rem; color: var(--ia-fg, #E7EAED); font-variant-numeric: tabular-nums; }
      .rank-rate small { font-size: 0.6rem; letter-spacing: 0.03em; color: var(--ia-fg-muted, #8A939D); text-transform: uppercase; }
      .rank-nodate { color: var(--ia-fg-muted, #8A939D) !important; }
      .rate-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 10px; padding: 10px 12px; margin-bottom: 10px; border: 1px solid var(--ia-line, rgba(255,255,255,0.07)); border-radius: 8px; background: rgba(255,255,255,0.02); }
      .rate-num { display: block; font-size: 1rem; color: var(--ia-fg, #E7EAED); font-variant-numeric: tabular-nums; }
      .rate-num.rate-span { font-size: 0.8rem; }
      .rate-cap { display: block; font-size: 0.6rem; letter-spacing: 0.04em; color: var(--ia-fg-muted, #8A939D); text-transform: uppercase; margin-top: 2px; }
      .rank-body { padding: 0 12px 12px 58px; }
      .rank-detail { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
      .rank-detail th { text-align: left; font-weight: 600; color: var(--ia-fg-muted, #8A939D); text-transform: uppercase; letter-spacing: 0.04em; font-size: 0.65rem; padding: 6px 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.08)); }
      .rank-detail td { padding: 6px 8px; color: var(--ia-fg-soft, #C3CAD1); border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.04)); font-variant-numeric: tabular-nums; }
      .tag-shared { display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 999px; font-size: 0.6rem; letter-spacing: 0.06em; color: var(--ia-warning-fg, #B29455); border: 1px solid rgba(178,148,85,0.45); }
      .rank-note { margin: 10px 0 0; font-size: 0.72rem; color: var(--ia-fg-muted, #8A939D); line-height: 1.5; }
      .rank-note.rank-warn { color: var(--ia-warning-fg, #B29455); }
      .rank-note strong { color: var(--ia-fg-soft, #C3CAD1); font-weight: 600; }

      ${CONTEXT_LOAD_STYLES}
      .b-pct { text-align: right; color: var(--ia-fg, #EDF1F4); font-variant-numeric: tabular-nums; }
      .link-more { margin-top: 12px; background: none; border: none; color: var(--ia-fg-soft, #9BA4AE); font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer; padding: 0; }
      .link-more:hover { color: var(--ia-fg, #DCE2E8); }

      .shortcuts { display: flex; flex-direction: column; gap: 9px; }
      .shortcut { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 11px; width: 100%; text-align: left; padding: 11px 12px; border-radius: 9px; background: var(--ia-bg-panel, #151A20); border: 1px solid var(--ia-line, rgba(255,255,255,0.07)); color: var(--ia-fg, #DCE2E8); cursor: pointer; font-family: inherit; }
      .shortcut:hover { background: var(--ia-bg-panel, #1A2027); border-color: var(--ia-line, rgba(255,255,255,0.14)); }
      .sc-ico { display: grid; place-items: center; width: 32px; height: 32px; border-radius: 8px; background: rgba(255,255,255,0.05); color: var(--ia-fg-soft, #A9B2BB); }
      .sc-ico .ico { width: 16px; height: 16px; }
      .sc-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .sc-text strong { font-size: 0.82rem; font-weight: 600; }
      .sc-text small { font-size: 0.7rem; color: var(--ia-fg-muted, #8A939D); }
      .sc-arrow { color: var(--ia-fg-muted, #8A939D); }

      .empty { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 6px; padding: 28px 12px; color: var(--ia-fg-muted, #8A939D); }
      .empty-ico .ico { width: 26px; height: 26px; color: #4E5862; }
      .empty-title { font-size: 0.85rem; color: var(--ia-fg-soft, #C3CAD1); font-weight: 600; }
      .empty-desc { font-size: 0.75rem; max-width: 280px; }

      .sk { min-height: 104px; display: flex; flex-direction: column; gap: 10px; justify-content: center; }
      .sk.tall { min-height: 200px; }
      .sk.tallest { min-height: 320px; }
      .sk-line { height: 10px; border-radius: 4px; background: linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.10), rgba(255,255,255,0.05)); background-size: 200% 100%; animation: sk 1.2s ease-in-out infinite; }
      .sk-line.tall { height: 24px; }
      .w30 { width: 30%; } .w40 { width: 40%; } .w60 { width: 60%; }
      @keyframes sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      @media (max-width: 1280px) {
        .grid-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      }
      @media (max-width: 1024px) {
        .grid-curve, .grid-3 { grid-template-columns: minmax(0, 1fr); }
        .status-body { grid-template-columns: 1fr; justify-items: center; }
        .status-list { width: 100%; }
        .rank-head { grid-template-columns: 30px minmax(100px, 1fr) 78px minmax(60px, 1.2fr) 46px 18px; }
        .rank-meta { display: none; }
        .rank-unavailable { grid-column: 3 / 6; }
      }
      @media (max-width: 720px) {
        .grid-4 { grid-template-columns: minmax(0, 1fr); }
        .filter, .filter-period { flex-basis: 100%; }
        .filter-actions { width: 100%; }
        /* Base de 170px: sem espaço para os dois lado a lado, cada botão ocupa a sua linha. */
        .filter-actions .btn { flex: 1 1 170px; justify-content: center; }
        .dash-head { flex-direction: column; align-items: stretch; }
        .head-actions { justify-content: space-between; }
        .chart-box { height: 220px; }
        .bars li { grid-template-columns: minmax(70px, 40%) 1fr 40px; }
        .rank-head { grid-template-columns: 26px 1fr 72px 44px 18px; }
        .rank-head .b-track { display: none; }
        .rank-unavailable { grid-column: 2 / 6; }
        .rank-body { padding-left: 12px; }
        .rank-detail { display: block; overflow-x: auto; }
      }
      @media (max-width: 420px) {
        .period-sep { display: none; }
        .period .ctl { flex-basis: 100%; min-width: 0; }
      }`;
  }
}

if (!customElements.get("app-overview-page")) {
  customElements.define("app-overview-page", OverviewPage);
}
